import { StepProps, formatMoney, toCentsFromString, useFlowState, useGraphQLQuery } from '@betterment/js-runtime';
import {
  Box,
  Button,
  CenteredLayout,
  FormikField,
  Heading,
  IconButton,
  Input,
  Label,
  Logo,
  PancakeStack,
  Select,
  Separator,
  Text,
  WorkflowHeader,
} from '@betterment/samba-react';
import { css } from '@betterment/samba-styled-system/css';
import { ErrorBoundary } from '@betterment/shared-components';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';

import { OrderUnitType, SaleScope, SellOrderProps } from './types';

import { estimatedMoneyAmountCopy, estimatedSharesCopy } from '../../direct-trading-accounts/utils';
import { WithLayout } from '../../RetailApp/components/WithLayout';
import { locationAssign } from '../../Shared/path-helpers';
import { DirectTradingAccountSellSecurityPositionsDocument } from '../graphql/directTradingAccountSellSecurityPositions.generated';
import { useOrderValidation } from '../hooks/useOrderValidation';

type FormValues = {
  order_amount: string;
  order_type: SaleScope;
  order_unit: OrderUnitType;
};

export const FormStep: React.FC<SellOrderProps & StepProps> = ({ goToNextStep, goToStep, accountId, securityId }) => {
  const { data } = useGraphQLQuery(DirectTradingAccountSellSecurityPositionsDocument, {
    variables: { accountId },
    throwOnError: true,
  });

  const { flowState } = useFlowState();

  const { validateOrder, loading: isMutationLoading } = useOrderValidation();

  if (!data) return null;

  const { account } = data;
  const securityPosition = account.securityPositions.find(sp => sp.financialSecurity.id === securityId);

  if (!securityPosition) return null;

  const { financialSecurity } = securityPosition;
  const price = financialSecurity.statistics.latestPrice.amount;

  const handleSubmit = async (values: FormValues, { setErrors, setSubmitting }: FormikHelpers<FormValues>) => {
    if (!values.order_amount) {
      return;
    }

    let orderSharesAmount;
    let orderDollarAmountCents;
    if (values.order_unit === OrderUnitType.Shares) {
      orderSharesAmount = Number(values.order_amount);
      orderDollarAmountCents = undefined;
    } else {
      orderSharesAmount = undefined;
      orderDollarAmountCents = toCentsFromString(values.order_amount);
    }

    const validationResult = await validateOrder({
      accountId: account.id,
      securityId,
      orderSharesAmount,
      orderDollarAmountCents,
      sellAll: values.order_type === SaleScope.SellAll,
    });

    if (validationResult) {
      const { displayableErrors, result } = validationResult;

      if (displayableErrors.length > 0) {
        const inputPath = values.order_unit === OrderUnitType.Dollars ? 'amount_cents' : 'shares';
        const error = displayableErrors.find(({ path }) => path.length === 1 && path[0] === inputPath)?.message;
        setErrors({ order_amount: error });
        setSubmitting(false);
        return;
      }

      if (result) {
        const nextStepData = {
          order_dollar_amount_cents: orderDollarAmountCents,
          order_shares_amount: orderSharesAmount,
          order_unit: values.order_unit,
          order_type: values.order_type,
          estimated_trade_timing: result.estimatedTradeTiming,
          tax_impact_preview: result.taxImpactPreview,
          sale_over_90_percent: result.saleOver90Percent,
        };

        if (result.saleOver90Percent) {
          goToNextStep(nextStepData);
        } else {
          goToStep('review', nextStepData);
        }
      }
    }
  };

  const handleReview = (formikProps: FormikProps<FormValues>) => {
    return formikProps.handleSubmit;
  };

  const handleSellAll = (formikProps: FormikProps<FormValues>) => {
    formikProps.setValues({
      ...formikProps.values,
      order_type: SaleScope.SellAll,
      order_unit: OrderUnitType.Shares,
      order_amount: String(securityPosition.shares),
    });

    return formikProps.handleSubmit;
  };

  const handleUnitValueChange = (input: OrderUnitType, formikProps: FormikProps<FormValues>) => {
    formikProps.setValues({
      ...formikProps.values,
      order_type: SaleScope.SellPartial,
      order_unit: input,
      order_amount: '',
    });
  };

  const placeholder = (formikProps: FormikProps<FormValues>) => {
    return formikProps.values.order_unit === OrderUnitType.Dollars ? 'Enter dollar amount' : 'Enter share amount';
  };

  const estimatedCopy = ({ values }: FormikProps<FormValues>) => {
    if (values.order_unit === OrderUnitType.Dollars) {
      return estimatedSharesCopy(toCentsFromString(values.order_amount), price);
    }

    return estimatedMoneyAmountCopy(Number(values.order_amount), price);
  };

  const initialValues: FormValues = {
    order_amount: '',
    order_unit: OrderUnitType.Dollars,
    order_type: SaleScope.SellPartial,
  };

  return (
    <ErrorBoundary>
      <WithLayout parts={{ sidebar: false, footer: true }}>
        <PancakeStack css={{ maxWidth: '100%', bg: 'background.surface' }}>
          <PancakeStack.Header
            css={{
              maxWidth: '100%',
              display: 'flex',
              justifyContent: 'center',
              borderBottomStyle: 'solid',
              borderBottomWidth: 'thin',
              borderColor: 'border',
            }}
          >
            <WorkflowHeader css={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
              <WorkflowHeader.Leading>
                <Logo css={{ height: 'size.300' }} type="symbol" />
              </WorkflowHeader.Leading>
              <WorkflowHeader.Title />
              <WorkflowHeader.Trailing>
                <IconButton
                  name="X"
                  label="Close"
                  spacing="compact"
                  onClick={() => locationAssign(flowState.returnToPath)}
                />
              </WorkflowHeader.Trailing>
            </WorkflowHeader>
          </PancakeStack.Header>
          <PancakeStack.Main css={{ paddingRight: 'space.200', paddingLeft: 'space.200' }}>
            <CenteredLayout size="medium" css={{ paddingTop: 'space.500', width: 'token(sizes.container.large)' }}>
              <Heading css={{ marginBottom: '24px' }}>Sell {financialSecurity.symbol}</Heading>
              <Formik
                onSubmit={handleSubmit}
                initialValues={initialValues}
                validateOnBlur={false}
                enableReinitialize={true}
              >
                {(formikProps: FormikProps<FormValues>) => (
                  <Form className={css({ width: '100%' })}>
                    <Box
                      css={{
                        width: '100%',
                        border: 'token(borderWidths.thin) solid token(colors.border)',
                        padding: 'space.300',
                        borderRadius: 'medium',
                      }}
                    >
                      <Box
                        css={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box css={{ alignSelf: 'center' }}>
                          <Text>Security</Text>
                        </Box>
                        <Box css={{ display: 'flex', flexDirection: 'column' }}>
                          <Text>{financialSecurity.name}</Text>
                          <Text css={{ alignSelf: 'end', color: 'text.weak' }} variant="text50">
                            {financialSecurity.symbol}
                          </Text>
                        </Box>
                      </Box>
                      <Separator
                        css={{
                          marginY: 'space.300',
                        }}
                      />
                      <Box
                        css={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box
                          css={{
                            display: 'flex',
                            alignItems: 'flex-start',
                          }}
                        >
                          <Box css={{ alignSelf: 'center' }}>
                            <Text css={{ marginRight: 'space.50' }}>Available</Text>
                          </Box>
                        </Box>
                        <Box css={{ display: 'flex', flexDirection: 'column' }}>
                          <Text>{formatMoney(securityPosition.amount)}</Text>
                          <Text css={{ alignSelf: 'end', color: 'text.weak' }} variant="text50">
                            {securityPosition.shares} shares
                          </Text>
                        </Box>
                      </Box>
                    </Box>
                    <Box
                      css={{
                        width: '100%',
                        border: 'token(borderWidths.thin) solid token(colors.border)',
                        padding: 'space.300',
                        marginY: 'space.300',
                        borderRadius: 'medium',
                      }}
                    >
                      <Label htmlFor="order_amount" css={{ mb: 'space.100' }}>
                        Amount
                      </Label>
                      <Box
                        css={{
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}
                      >
                        <Box css={{ flexGrow: '4', marginRight: 'space.100' }}>
                          <FormikField name="order_amount">
                            <FormikField.Control>
                              <Input
                                appearance="default"
                                spacing="default"
                                leadingSymbol={
                                  formikProps.values.order_unit === OrderUnitType.Dollars ? '$' : undefined
                                }
                                placeholder={placeholder(formikProps)}
                                id="order_amount"
                                value={formikProps.values.order_amount}
                              />
                            </FormikField.Control>
                            <FormikField.ErrorMessage />
                          </FormikField>
                        </Box>
                        <Box css={{ flexGrow: '1' }}>
                          <FormikField name="order_unit">
                            <FormikField.Control>
                              <Select
                                onValueChange={(input: OrderUnitType) => handleUnitValueChange(input, formikProps)}
                              >
                                <Select.Trigger appearance="default" aria-label="units">
                                  <Select.Value />
                                </Select.Trigger>
                                <Select.Content>
                                  <Select.Item value="dollars">Dollars</Select.Item>
                                  <Select.Item value="shares">Shares</Select.Item>
                                </Select.Content>
                              </Select>
                            </FormikField.Control>
                            <FormikField.ErrorMessage />
                          </FormikField>
                        </Box>
                      </Box>
                      <Text css={{ color: 'text.weak' }} variant="text50">
                        {estimatedCopy(formikProps)}
                      </Text>
                    </Box>
                    <Box
                      css={{
                        display: 'flex',
                      }}
                    >
                      <Button
                        type="submit"
                        onClick={() => handleReview(formikProps)}
                        variant="solidPrimary"
                        value="single"
                        css={{ px: 'space.800' }}
                        loading={isMutationLoading}
                      >
                        Continue
                      </Button>
                      <Button
                        type="submit"
                        onClick={() => handleSellAll(formikProps)}
                        css={{ mx: 'space.300', my: 'space.200' }}
                        variant="textPrimary"
                        value="all"
                      >
                        Sell all shares
                      </Button>
                    </Box>
                  </Form>
                )}
              </Formik>
            </CenteredLayout>
          </PancakeStack.Main>
        </PancakeStack>
      </WithLayout>
    </ErrorBoundary>
  );
};
